import { Component, ElementRef, inject, viewChild, signal, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

// TAS Search Library - Models
import { SearchResponse, StreamResponse } from 'aisearch-search-fe-library';
// TAS Search Library - Services
import { SearchService } from 'aisearch-search-fe-library';

// Components
import { SearchInputComponent } from '../../shared-components/search-input/search-input.component';
import { AnswerComponent } from '../../shared-components/answer/answer.component';
import { ResultsComponent } from '../../shared-components/results/results.component';
import { FollowupHistoryComponent } from '../../shared-components/followup-history/followup-history.component';

// NG-Bootstrap
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';

// Font Awesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';

//Services
import { ContextService } from '../../../contexts/services/context.service';
import { MarkdownService } from 'ngx-markdown';


@Component({
  selector: 'app-search-preview',
  imports: [
    ReactiveFormsModule,
    FontAwesomeModule,
    // Components
    SearchInputComponent,
    AnswerComponent,
    ResultsComponent,
    FollowupHistoryComponent
  ],
  templateUrl: './search-preview.component.html',
  styleUrl: './search-preview.component.scss'
})
export class SearchPreviewComponent implements OnInit {
  activeOffcanvas = inject(NgbActiveOffcanvas);
  private readonly searchSrv = inject(SearchService);
  private readonly contextSrv = inject(ContextService);
  private readonly markdownSrv = inject(MarkdownService);

  faCircle = faCircle;

  readonly resultsSection = viewChild.required<ElementRef>('results');

  @Input() contextId!: string;

  sessionId!: string;

  loading = signal(false);
  followupLoading = signal(false);
  query = signal('');
  answer = signal('');
  searchResults = signal<any[]>([]);
  followUps = signal<{ question: string; answer: string; textWithCitations: string }[]>([]);
  textWithCitations!: string;

  ngOnInit(): void {
    this.searchSrv.setContextId(this.contextId);
  }

  makeSearch(query: string, stream = false): void {
    this.resetState(query);
    this.loading.set(true);

    const request = stream
      ? this.searchSrv.streamWithEventSource(query)
      : this.searchSrv.search(query);

    request.subscribe({
      next: res => this.handleResponse(res),
      error: err => {
        this.loading.set(false);
        console.error(`${stream ? 'Stream' : 'Search'} error:`, err);
      }
    });
  }

  makeFollowup(query: string, stream = false): void {
    this.appendFollowup();
    this.query.set(query);
    this.answer.set('');
    this.textWithCitations = '';
    this.followupLoading.set(true);

    const request = stream
      ? this.searchSrv.streamWithEventSource(query, this.sessionId)
      : this.searchSrv.search(query, this.sessionId);

    request.subscribe({
      next: res => this.handleResponse(res),
      error: err => {
        this.followupLoading.set(false);
        console.error(`${stream ? 'Stream' : 'Followup'} error:`, err);
      }
    });

    setTimeout(() => {
      this.resultsSection().nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  }

  private async handleResponse(res: SearchResponse | StreamResponse): Promise<void> {
    const currentAnswer = this.answer();

    if (res.answer) {
      const combinedAnswer = currentAnswer + res.answer;
      this.answer.set(combinedAnswer);

      // Aggiorna i loading solo se c'è una risposta
      this.loading.set(false);
      this.followupLoading.set(false);
    }

    if (res.results) {
      this.searchResults.set(res.results);
    }

    if (res.citations?.length > 0) {
      const citationsMarkdown = this.searchSrv.makeCitationTags(res.answer, res.citations, res.results);
      const parsedCitationsMarkdown = await this.markdownSrv.parse(citationsMarkdown.textWithTags);
      this.textWithCitations = this.renderCitationSpans(parsedCitationsMarkdown, citationsMarkdown.citationsList);
    }

    if (res.session?.id) {
      this.sessionId = res.session.id;
    }
  }

  private renderCitationSpans(
    textHtmlWithTags: string,
    citationsList: {
      citationTag: string;
      referenceIndexes: number[];
      reference: string;
    }[]
  ) {
    // Attendi il parsing markdown
    let renderedText = textHtmlWithTags;

    const svgIcon = `<img src="/images/icons/fontawesome/link-solid-full.svg" alt="link" height="14" style="position: relative; bottom: 1px;" />`;

    // Sostituisci i tag con gli span
    citationsList.forEach((citation) => {
      const refIndexStr = citation.referenceIndexes.join(',');
      const span = ` <span class="btn btn-primary rounded-pill p-0 px-2 mx-1 tas-anchor-icon-button tas-anchor-icon-tooltip">
            <i>${svgIcon}</i>
            <span class="tas-anchor-icon-tooltip-text">
              ${citation.reference.replace(/\n/g, '<br />')}
            </span>
          </span>`
      renderedText = renderedText.split(citation.citationTag).join(span);
    });

    // Sanifica il risultato
    return renderedText;
  }

  private appendFollowup(): void {
    if (this.query() && this.answer()) {
      this.followUps.set([
        ...this.followUps(),
        {
          question: this.query(),
          answer: this.answer(),
          textWithCitations: this.textWithCitations
        }
      ]);
    }
  }

  private resetState(query: string): void {
    this.query.set(query);
    this.answer.set('');
    this.searchResults.set([]);
    this.textWithCitations = '';
    this.followUps.set([]);
  }
}
